import { useState } from 'react';
import {
  CommandGroup,
  CommandEmpty,
} from '@/components/ui/command';
import { SearchableDropdown } from '@/components/ui/searchable-dropdown';
import { DropdownTrigger } from '@/react/components/ui/dropdown-trigger';
import { DropdownListItem } from '@/react/components/ui/dropdown-list-item';
import { SearchableDropdownCreateButton } from '@/react/components/ui/searchable-dropdown-create-button';

import { useSearchContacts } from '@hooks/contacts';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useTranslation } from '@hooks/useTranslation';
import type { ApiContact } from '@api/contacts';
import { HighlightMatch } from '@components/common/HighlightMatch';

const SEARCH_DEBOUNCE_MS = 300;

interface CustomerEmailComboboxProps {
  onSelect: (contact: ApiContact) => void;
  onCreateNew: () => void;
  selectedContact: ApiContact | null;
  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  invalid?: boolean;
}

export function CustomerEmailCombobox({
  onSelect,
  onCreateNew,
  selectedContact,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  invalid = false,
}: CustomerEmailComboboxProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, SEARCH_DEBOUNCE_MS);

  const { data: contacts, isFetching } = useSearchContacts(debouncedSearchQuery);
  // Treat the term as pending while the debounce is still catching up or the
  // request is in flight. Without this the previous query's cached results
  // (keepPreviousData) flash when the list becomes visible at the threshold.
  const isPending = isFetching || searchQuery !== debouncedSearchQuery;

  return (
    <SearchableDropdown
      isOpen={isOpen}
      onOpenChange={setIsOpen}
      searchPlaceholder={t('shared.search.customer_email')}
      onSearchChange={setSearchQuery}
      shouldFilter={false}
      showSearchIcon={false}
    >
      <SearchableDropdown.Trigger>
        <DropdownTrigger
          selectedLabel={selectedContact?.email}
          placeholder={t('create_order.placeholders.email')}
          isOpen={isOpen}
          invalid={invalid}
        />
      </SearchableDropdown.Trigger>

      <SearchableDropdown.List
        showFadeGradient
        onReachEnd={hasMore && !isLoadingMore ? onLoadMore : undefined}
        isLoadingMore={isLoadingMore}
      >
        {isPending && (
          <div className="p-4 text-center text-sm text-grey" role="status" aria-live="polite">{t('shared.loading')}</div>
        )}

        {!isPending && (
          <CommandEmpty className="px-4 py-3 text-left text-sm text-grey">
            {t('shared.empty.no_customers')}
          </CommandEmpty>
        )}

        {!isPending && contacts && contacts.length > 0 && (
          <CommandGroup className="overflow-visible p-0">
            <div className="flex flex-col gap-2 py-2">
              {contacts.map((contact) => (
                <DropdownListItem
                  key={contact.id}
                  value={String(contact.id)}
                  onSelect={() => {
                    onSelect(contact);
                    setIsOpen(false);
                  }}
                  primary={<HighlightMatch text={contact.email} query={debouncedSearchQuery} />}
                  primaryTitle={contact.email}
                  secondary={
                    <>
                      {contact.full_name}
                      {contact.mobile_number && <> · {contact.mobile_number}</>}
                      {contact.organisation?.name && <> · {contact.organisation.name}</>}
                    </>
                  }
                  secondaryTitle={[contact.full_name, contact.mobile_number, contact.organisation?.name]
                    .filter(Boolean)
                    .join(' · ')}
                />
              ))}
            </div>
          </CommandGroup>
        )}
      </SearchableDropdown.List>

      <SearchableDropdown.Footer>
        <SearchableDropdownCreateButton
          label={t('shared.create_new_customer')}
          onSelect={() => { setIsOpen(false); onCreateNew(); }}
        />
      </SearchableDropdown.Footer>
    </SearchableDropdown>
  );
}
